import { LegalStructureEntityRole } from '../../../../services/queries/MaggieLegalStructureQueries';
import { StructureRole } from './roleColors';
import { RelationshipType } from './types';

/**
 * How a payload node becomes a card's display role, and which edge types the chart draws.
 *
 * Extracted from `useStructureChartGraph` (FD-4933) so `coHolders` can build a merged card
 * the same way the rooted graph builds its own — a co-holder that resolved its role by a
 * second set of rules would be a differently-coloured card for the same entity. Its own
 * module rather than an export from the hook, because the hook imports the merge: sharing
 * through it would be a cycle. Same reason `nodeKey` lives alone.
 */

/** `DisplayEntityKind` from the graph payload to the chart's display role. */
export const ROLE_BY_ENTITY_KIND: Record<string, StructureRole> = {
  // A family heads a band; it reaches the payload as a root or a holder's parent.
  FAMILY: 'FUND_FAMILY',
  FUND: 'MAIN_FUND',
  HOLDING_CO: 'HOLDING_CO',
  BLOCKER: 'BLOCKER_CORP',
  SPV: 'CO_INVESTMENT',
  GP_ENTITY: 'GENERAL_PARTNER',
  MANAGEMENT_CO: 'CARRY_CO_INVEST',
  PORTFOLIO_COMPANY: 'PORTFOLIO_COMPANY',
};

/**
 * The register's `EntityRole` to the chart's display role.
 *
 * MANAGEMENT_CO collapses onto the carry/co-invest tag because the prototype's card
 * vocabulary has no separate management-company role. FAMILY heads a band rather than
 * sitting among the vehicles, so it takes the quietest card treatment (FD-4480).
 */
export const ROLE_BY_ENTITY_ROLE: Record<LegalStructureEntityRole, StructureRole | null> = {
  MAIN_FUND: 'MAIN_FUND',
  FEEDER_PARALLEL: 'FEEDER_PARALLEL',
  CARRY_CO_INVEST: 'CARRY_CO_INVEST',
  HOLDING_CO: 'HOLDING_CO',
  BLOCKER: 'BLOCKER_CORP',
  SPV: 'CO_INVESTMENT',
  GP_ENTITY: 'GENERAL_PARTNER',
  MANAGEMENT_CO: 'CARRY_CO_INVEST',
  FAMILY: 'FUND_FAMILY',
};

export function toRole(node: { kind?: string | null; fundKind?: 'fund' | 'intermediary' }): StructureRole {
  // Every fund node comes back as kind FUND, including the intermediary funds paired
  // to holding entities. Without fundKind a blocker-holding fund would read MAIN FUND.
  if (node.kind === 'FUND' && node.fundKind === 'intermediary') {
    return 'HOLDING_CO';
  }

  return ROLE_BY_ENTITY_KIND[node.kind ?? ''] ?? 'HOLDING_CO';
}

/**
 * The chart's grammar is the API's `RelationshipType`, so this is a membership check
 * rather than a translation. An unrecognised value means the API grew a type the
 * chart has no line for, and is dropped rather than guessed at.
 */
export const KNOWN_RELATIONSHIP_TYPES = new Set<RelationshipType>([
  'EQUITY',
  'LOAN',
  'GP_COMMITMENT',
  'CARRY',
  'MANAGEMENT',
  'VOTING',
  'LP_COMMITMENT',
]);
